import { type FriendsMetric, LOWER_IS_BETTER } from "./FRIENDS_METRICS";
import type { FriendPlayer, PeriodStats } from "./FriendsBoard";
import { metricValue } from "./metricValue";

export type RankedPlayer = {
  player: FriendPlayer;
  // Место 1…n; нет значения (нет матчей за период) — `null`, такие в конце.
  place: number | null;
  value: number | null;
};

// Таблица по показателю: лучшие сверху, без значения — в конце в исходном порядке.
// `pick` выбирает окно: текущий период или он же неделю назад.
export const rankPlayers = (
  players: readonly FriendPlayer[],
  metric: FriendsMetric,
  pick: (player: FriendPlayer) => PeriodStats = (player) => player.current,
): RankedPlayer[] => {
  const sign = LOWER_IS_BETTER.has(metric) ? 1 : -1;
  const withValues = players.map((player) => ({
    player,
    value: metricValue(metric, pick(player)),
  }));
  const placed = withValues
    .filter((row): row is { player: FriendPlayer; value: number } => row.value !== null)
    .sort((a, b) => sign * (a.value - b.value));
  const unplaced = withValues.filter((row) => row.value === null);
  return [
    ...placed.map((row, index) => ({ ...row, place: index + 1 })),
    ...unplaced.map((row) => ({ ...row, place: null })),
  ];
};

// Сдвиг места за неделю: плюс — поднялся. Неделю назад или сейчас без места — `null`.
export const placeMoves = (
  players: readonly FriendPlayer[],
  metric: FriendsMetric,
): Map<number, number | null> => {
  const now = rankPlayers(players, metric);
  const before = new Map(
    rankPlayers(players, metric, (player) => player.week_ago).map((row) => [
      row.player.account_id,
      row.place,
    ]),
  );
  return new Map(
    now.map((row) => {
      const was = before.get(row.player.account_id) ?? null;
      return [row.player.account_id, row.place === null || was === null ? null : was - row.place];
    }),
  );
};
